import type {
	FieldArrayRenderProps,
	FormRenderProps,
} from "@tql/ui-components-react/kendo/form";
import { FieldArray } from "@tql/ui-components-react/kendo/form";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import type { SortableItemUIProps } from "@tql/ui-components-react/kendo/sortable";
import { Sortable } from "@tql/ui-components-react/kendo/sortable";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { FormElement, FormField } from "@tql/ui-components-react/tql/form/beta";
import {
	Checkbox,
	NumericTextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import { markerPin_01 } from "@tql/ui-components-react/untitled/icons";
import { createContext, useCallback, useContext, useMemo } from "react";

import type { Stop, StopLocation } from "~/features/ftl/shared/ftl-stop";
import { createNewStop } from "~/features/ftl/shared/ftl-stop.ts";

import {
	loadValueValidator,
	temperatureValidator,
	weightValidator,
} from "../../create-ftl-quote-validators.ts";
import styles from "./quote-details-step.module.css";
import { QuoteSortableItemContainer } from "./sortable-item-ui/sortable-item-ui.tsx";

type LoadValue = {
	code: string;
	displayName: string;
};

type TrailerType = {
	code: string;
	displayName: string;
	isRefrigerated: boolean;
};

export type StopSortableProps = {
	stops: Stop[];
	onRemove: (index: number) => void;
	onCopy: (index: number, locationOptions?: StopLocation[]) => void;
	onResetForm?: () => void;
};

type QuoteDetailsStepProps = {
	formRenderProps: FormRenderProps;
	trailerTypes: TrailerType[] | null | undefined;
	isLoadingTrailerTypes: boolean;
	loadValues: LoadValue[] | null | undefined;
	isLoadingLoadValues: boolean;
	isSubmitting?: boolean;
};

export const sortableItemUIContext = createContext<StopSortableProps>(
	{} as StopSortableProps,
);

type StopsFormActions = {
	onFormReset: () => void;
	valid: boolean;
	getStops: () => Stop[];
	setStops: (stops: Stop[]) => void;
	onSubmit: FormRenderProps["onSubmit"];
	isSubmitting: boolean;
};

const stopsFormActionsContext = createContext<StopsFormActions | null>(null);

function trailerTypeValidator(value: string): string {
	if (!value) {
		return "Trailer Type is required.";
	}

	return "";
}

// 100 is the width of the downstream Tender table column
// (tblTraxTenderALoadData.Commodity). The Quote table column is wider (255),
// but the commodity cannot be edited on the tender step, so the narrower limit
// is applied here.
const COMMODITY_MAX_LENGTH = 100;

function commodityValidator(value: string): string {
	if (!value) {
		return "Commodity is required.";
	}
	if (value.length > COMMODITY_MAX_LENGTH) {
		return `Commodity Description cannot exceed ${COMMODITY_MAX_LENGTH} characters.`;
	}

	return "";
}

function SortableItemUI(props: Readonly<SortableItemUIProps>) {
	return <QuoteSortableItemContainer {...props} />;
}

type StopsContainerProps = FieldArrayRenderProps;

function StopsContainer({ value, ...props }: Readonly<StopsContainerProps>) {
	const stops = value as Stop[];
	const formActions = useContext(stopsFormActionsContext);

	const onRemove = useCallback(
		(index: number) => {
			props.onRemove({ index });
		},
		[props],
	);

	const onCopy = useCallback(
		(index: number, locationOptions?: StopLocation[]) => {
			const stopToCopy = stops[index];
			if (stopToCopy) {
				const newStop = {
					...stopToCopy,
					id: crypto.randomUUID(),
					orderIndex: index + 2,
					locationOptions,
				};

				props.onPush({ value: newStop });

				setTimeout(() => {
					if (!formActions) {
						return;
					}

					const currentStops = formActions.getStops();
					if (currentStops.length > 0) {
						const lastStop = currentStops.at(-1);
						if (lastStop) {
							const reorderedStops = [
								...currentStops.slice(0, index + 1),
								lastStop,
								...currentStops.slice(index + 1, -1),
							];
							formActions.setStops(reorderedStops);
						}
					}
				}, 0);
			}
		},
		[formActions, props, stops],
	);

	const addStop = useCallback(() => {
		props.onPush({ value: createNewStop() });
	}, [props]);

	const contextValue = useMemo(
		() => ({
			stops,
			onRemove,
			onCopy,
		}),
		[stops, onRemove, onCopy],
	);

	const handleAddStopKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === " " || e.key === "Enter") {
			e.preventDefault();
			if (e.repeat) return;
			addStop();
		}
	};

	const isSubmitting = formActions?.isSubmitting ?? false;

	const handleGetQuoteKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === " " || e.key === "Enter") {
			// preventDefault must run even on repeat, otherwise the browser's native
			// Enter-to-click still reaches onClick.
			e.preventDefault();
			if (e.repeat || isSubmitting) return;
			formActions?.onSubmit(e);
		}
	};

	return (
		<>
			<div className={styles.section}>
				<h3 className={styles.sectionTitle}>Pickup and Delivery</h3>
				<div className={styles.routeStops}>
					<sortableItemUIContext.Provider value={contextValue}>
						<Sortable
							data={stops}
							idField={"id"}
							itemUI={SortableItemUI}
							onDragOver={props.onMove}
						/>
					</sortableItemUIContext.Provider>
					<div
						className={`k-d-flex k-justify-content-end k-pt-4 ${styles.addStopCard}`}
					>
						<div>
							<Button
								formAction={"none"}
								onClick={addStop}
								onKeyDown={handleAddStopKeyDown}
								svgIcon={markerPin_01}
								variant={"secondary"}
							>
								Add Stop
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className={"k-pb-5"}>
				<div className={"k-d-flex k-justify-content-end k-gap-2"}>
					<div className={styles.addStopInline}>
						<Button
							formAction={"none"}
							onClick={addStop}
							onKeyDown={handleAddStopKeyDown}
							svgIcon={markerPin_01}
							variant={"secondary"}
						>
							Add Stop
						</Button>
					</div>
					<Button
						data-testid={"get-ftl-quote-button"}
						formAction={"none"}
						loading={isSubmitting}
						onClick={(e) => {
							if (isSubmitting) return;
							formActions?.onSubmit(e);
						}}
						onKeyDown={handleGetQuoteKeyDown}
						variant={"primary"}
					>
						Get Quote
					</Button>
				</div>
			</div>
		</>
	);
}

export function QuoteDetailsStep(props: Readonly<QuoteDetailsStepProps>) {
	const formActions = useMemo<StopsFormActions>(
		() => ({
			onFormReset: () => {
				props.formRenderProps.onFormReset();
			},
			valid: props.formRenderProps.valid,
			getStops: () => props.formRenderProps.valueGetter("stops") as Stop[],
			setStops: (stops: Stop[]) => {
				props.formRenderProps.onChange("stops", {
					value: stops,
				});
			},
			onSubmit: props.formRenderProps.onSubmit,
			isSubmitting: props.isSubmitting ?? false,
		}),
		[props.formRenderProps, props.isSubmitting],
	);

	return (
		<FormElement>
			<div className={"k-pl-5 k-pt-5 k-pr-5"}>
				<div className={styles.section}>
					<h3 className={styles.sectionTitle}>Load Information</h3>
					<div className={styles.quoteInfo}>
						<div
							className={styles.trailerType}
							data-testid={"trailer-type"}
							id={"trailer-type"}
						>
							<FormField
								required
								component={DropDownList}
								data={props.trailerTypes ?? undefined}
								dataItemKey={"code"}
								label={"Trailer Type"}
								loading={props.isLoadingTrailerTypes}
								name={"trailerType"}
								textField={"displayName"}
								validator={trailerTypeValidator}
							/>
						</div>
						{(props.formRenderProps.valueGetter("trailerType") as TrailerType)
							?.isRefrigerated === true && (
							<div
								className={styles.temperature}
								data-testid={"temperature"}
								id={"temperature"}
							>
								<FormField
									required
									component={NumericTextBox}
									format={"n0"}
									label={"Temperature (°F)"}
									max={99}
									min={-99}
									name={"temperature"}
									placeholder={"Enter temperature"}
									validator={temperatureValidator}
								/>
							</div>
						)}
						<div
							className={styles.loadValue}
							data-testid={"load-value"}
							id={"load-value"}
						>
							<FormField
								required
								component={DropDownList}
								data={props.loadValues ?? undefined}
								dataItemKey={"code"}
								label={"Load Value"}
								loading={props.isLoadingLoadValues}
								name={"loadValue"}
								textField={"displayName"}
								validator={loadValueValidator}
							/>
						</div>
						<div
							className={styles.commodity}
							data-testid={"commodity"}
							id={"commodity"}
						>
							<FormField
								required
								component={Input}
								label={"Commodity Description"}
								name={"commodity"}
								placeholder={"Enter commodity"}
								validator={commodityValidator}
							/>
						</div>
						<div
							className={styles.weight}
							data-testid={"total-weight"}
							id={"total-weight"}
						>
							<FormField
								required
								component={NumericTextBox}
								format={"n0"}
								label={"Total Weight (lbs.)"}
								max={999_999}
								min={1}
								name={"totalWeight"}
								validator={weightValidator}
							/>
						</div>
						<div className={styles.hazmat} data-testid={"hazmat"} id={"hazmat"}>
							<FormField
								component={Checkbox}
								label={"Hazmat"}
								name={"hazmat"}
							/>
						</div>
					</div>
				</div>
				<div id={"stops-container"}></div>
				<stopsFormActionsContext.Provider value={formActions}>
					<FieldArray component={StopsContainer} name={"stops"} />
				</stopsFormActionsContext.Provider>
			</div>
		</FormElement>
	);
}
